import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { DzSliderComponent } from '@blk/daisy/combinations/slider';
import { DZ_COLOR_GREEN_100, DZ_COLOR_GREEN_200, DZ_COLOR_GREEN_300, DZ_COLOR_GREEN_400, DZ_COLOR_GREEN_500 } from '@blk/daisy/core/styles';
import { TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { ModelsTransformUtil } from '@ddt-web/common';
import { InjectConfig, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import { ComponentData } from '@ddt-web/data-service';
import { SharedApplicationConstants } from '@ddt-web/domain';
import * as isEmpty from 'lodash/isEmpty';

import { ModelsSliderData } from '../models';
import { RiskProfile } from './risk-profile.enum';

@Component({
	selector: 'mb-models-slider',
	templateUrl: './models-slider.component.html',
	styleUrls: ['./models-slider.component.scss']
})
export class ModelsSliderComponent implements OnChanges, AfterViewInit {
	@Input() public data: ComponentData;
	@Input() public selectedModelId: string;
	@Input() public selectedCardType: string;
	@Input() public routedFromHome: boolean = false;
	@Input() public selectedModelGroupId: string;
	@Output() public modelChanged: EventEmitter<string> = new EventEmitter<string>();
	@ViewChild(DzSliderComponent) public slider: DzSliderComponent;
	@InjectConfig(ModelsExperienceConfigKey.MODELS_DISPLAY_ORDER_ON_SLIDER_ON_RISK_PROFILE) private readonly displayOrderOnBasisOfRiskProfile: { [key: string]: number } = {};

	public RiskProfile = RiskProfile;
	public portfolioTransformedData: ModelsSliderData[] = [];
	public customRangeLabelObject = { minRangeLabel: '', midRangeLabel: '', maxRangeLabel: '' };
	public min: number = 0;
	public max: number;
	public defaultValue: number = 0;
	public selectedRiskProfileValue: string = '';
	public selectedModelShortName: string = '';
	public tealiumConstants = TealiumConstants;
	public sliderLoading: boolean = true;
	private sliderBarColourConfig = {
		conservative: {
			percent: 0,
			colour: DZ_COLOR_GREEN_100
		},
		moderate_conservative: {
			percent: 0,
			colour: DZ_COLOR_GREEN_200
		},
		moderate: {
			percent: 0,
			colour: DZ_COLOR_GREEN_300
		},
		moderate_aggressive: {
			percent: 0,
			colour: DZ_COLOR_GREEN_400
		},
		aggressive: {
			percent: 0,
			colour: DZ_COLOR_GREEN_500
		}
	};

	constructor(private tealiumAnalyticsService: TealiumAnalyticsService) { }

	public ngAfterViewInit(): void {
		if (this.slider) {
			(this.slider as any).setSliderBackground = () => null;
			this.sliderBarGradient();
		}
	}

	public ngOnChanges(): void {
		let portfolioArray = this.data.success ? this.data.getTabById('model.slider')['tableData'][0]['rows'] : [];
		portfolioArray = this.filterPortfolioArrayBasedOnGroup(portfolioArray);
		const estimatedRiskArray = this.data.success ? this.data.getTabById('model.slider.overview')['tableData'][0]['rows'] : [];
		const estimatedRiskMap = new Map();
		estimatedRiskArray.forEach(item => estimatedRiskMap.set(item.id.formattedValue, item.estimatedRisk.rawValue));
		this.max = portfolioArray.length ? portfolioArray.length - 1 : 0;
		this.portfolioTransformedData = portfolioArray.map((item) => {
			const riskProfile = (item.riskProfile.rawValue as string).split(' ').join('_');
			return Object.assign({}, {
				id: item.id.formattedValue,
				portfolioId: item.portfolioId.formattedValue,
				shortName: item.shortName.formattedValue,
				riskProfile,
				displayOrder: this.displayOrderOnBasisOfRiskProfile[riskProfile],
				estimatedRisk: estimatedRiskMap.get(item.portfolioId.formattedValue),
				dasModelOrder: <number>item.dasModelOrder.rawValue
			});
		});
		this.sliderLoading = false;
		this.portfolioTransformedData = ModelsTransformUtil.sortModelPortfolios(this.portfolioTransformedData, 'dasModelOrder');

		this.selectedModelId = this.selectedModelId === '' ? this.portfolioTransformedData[0].portfolioId : this.selectedModelId;
		const modelIndex = this.portfolioTransformedData.findIndex(item => item.portfolioId === this.selectedModelId);
		this.defaultValue = modelIndex >= 0 ? modelIndex : 0;
		if (this.portfolioTransformedData.length === 1) {
			this.selectedRiskProfileValue = this.portfolioTransformedData[0].riskProfile;
			this.selectedModelShortName = this.portfolioTransformedData[0].shortName;
			this.modelChanged.emit(this.portfolioTransformedData[0].portfolioId);
		}
	}

	public sliderValueChangeHandler(val: number) {
		const index = val > this.portfolioTransformedData.length ? 0 : val;
		this.defaultValue = index;
		this.selectedRiskProfileValue = this.portfolioTransformedData[index]?.riskProfile;
		this.selectedModelShortName = this.portfolioTransformedData[index]?.shortName;

		const eventName = TealiumConstants.MODELS_EXPERIENCE_EVENTS.MODELS_EXPERIENCE_APP_NAME + ':' + TealiumConstants.MODELS_EXPERIENCE_EVENTS.FIND_AND_COMPARE_PAGE + `:mini compare modal:model slider changed:${this.selectedRiskProfileValue}:${this.selectedModelShortName}`;
		this.tealiumAnalyticsService.trackEventTealium(new TealiumAnalyticsEvent(eventName, {
			toolName: TealiumConstants.TOOL_NAMES.MODELS_EXPERIENCE
		}));
		this.modelChanged.emit(this.portfolioTransformedData[index]?.portfolioId);
	}

	private sliderBarGradient(): void {
		this.sliderBarColourConfig.conservative.percent = this.calculatePercentage(this.displayOrderOnBasisOfRiskProfile[this.RiskProfile.Conservative]);
		this.sliderBarColourConfig.moderate_conservative.percent = this.calculatePercentage(this.displayOrderOnBasisOfRiskProfile[this.RiskProfile.ModerateConservative]);
		this.sliderBarColourConfig.moderate.percent = this.calculatePercentage(this.displayOrderOnBasisOfRiskProfile[this.RiskProfile.Moderate]);
		this.sliderBarColourConfig.moderate_aggressive.percent = this.calculatePercentage(this.displayOrderOnBasisOfRiskProfile[this.RiskProfile.ModerateAggressive]);
		this.sliderBarColourConfig.aggressive.percent = this.calculatePercentage(this.displayOrderOnBasisOfRiskProfile[this.RiskProfile.Aggressive]);
		const gradientString = Object.keys(this.sliderBarColourConfig).reduce((acc, currentValue, index, array) => {
			const percentCovered = array.filter((item, itemIndex) => itemIndex < index)
				.reduce((prev, curr) => {
					return prev + this.sliderBarColourConfig[curr]?.percent;
				}, 0) ?? 0;

			return acc += ` ${this.sliderBarColourConfig[currentValue].colour} ${percentCovered}%,
			 ${this.sliderBarColourConfig[currentValue].colour} ${this.sliderBarColourConfig[currentValue].percent + percentCovered}%,`;
		}, '');
		setTimeout(() => {
			if (this.slider?.inputRange) { this.slider.inputRange.nativeElement.style = `background: linear-gradient(90deg,${gradientString.replace(/,\s*$/, '')})`; }
		}, 1);
	}

	private calculatePercentage(displayOrder: number): number {
		return Math.abs((this.portfolioTransformedData.filter(item => item.displayOrder === displayOrder).length / this.portfolioTransformedData.length) * 100);
	}

	private filterPortfolioArrayBasedOnGroup(portfolioArray): any {
		const groupId = this.routedFromHome || isEmpty(this.selectedModelGroupId) ? SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP : this.selectedModelGroupId;
		return portfolioArray?.filter(cell => {
			if (!isEmpty(cell?.groups?.formattedValue)) {
				return cell.groups.formattedValue.some(group => group.id === groupId);
			}
			return false;
		});
	}
}
